import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '@/lib/axios';
import { Modal } from '@/components';
import Input from '@/components/Input';
import Label from '@/components/Label';
import toast from 'react-hot-toast';
import { TEMPLATE_LIBRARY_QUERY_KEYS } from '../hooks/useTemplateLibraryQuery';

interface AddIndustryModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryKey: string;
  onSuccessAdd?: (industryId: string) => void;
}

export const AddIndustryModal: React.FC<AddIndustryModalProps> = ({ isOpen, onClose, categoryKey, onSuccessAdd }) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (payload: { categoryKey: string; label: string; key: string }) => {
      const response = await axiosInstance.post('/wp-crm/templates/library/industries', payload);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success('Industry added successfully');
      queryClient.invalidateQueries({ queryKey: TEMPLATE_LIBRARY_QUERY_KEYS.all });
      if (onSuccessAdd && data?.data?._id) {
        onSuccessAdd(data.data._id);
      }
      formik.resetForm();
      onClose();
    }
  });

  const formik = useFormik({
    initialValues: {
      label: '',
    },
    validationSchema: Yup.object({
      label: Yup.string().required('Label is required').max(100, 'Max 100 characters'),
    }),
    onSubmit: (values) => {
      const key = values.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      mutation.mutate({
        categoryKey: categoryKey.toLowerCase(),
        label: values.label,
        key,
      });
    },
  });

  React.useEffect(() => {
    if (!isOpen) {
      formik.resetForm();
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Industry"
      actions={[
        {
          label: mutation.isPending ? 'Adding...' : 'Add',
          color: 'primary',
          disabled: mutation.isPending,
          loading: mutation.isPending,
          onClick: () => {
            formik.handleSubmit();
          }
        }
      ]}
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label className="mb-0 block text-xs font-semibold text-gray-600 dark:text-gray-400 tracking-wide">
            Category
          </Label>
          <Input value={categoryKey} disabled className="bg-gray-50 dark:bg-gray-800/50" />
        </div>

        <div className="space-y-1.5">
          <Label className="mb-0 block text-xs font-semibold text-gray-600 dark:text-gray-400 tracking-wide">
            Industry Label
          </Label>
          <Input
            name="label"
            value={formik.values.label}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="e.g. Real Estate"
            error={!!(formik.touched.label && formik.errors.label)}
            helperText={formik.touched.label && typeof formik.errors.label === 'string' ? formik.errors.label : undefined}
          />
        </div>
      </div>
    </Modal>
  );
};
